import {useEffect, useMemo, useRef} from "react";
import {useDispatch, useSelector} from "react-redux";
import {css} from "@panda/css";
import type {Dispatch, UnknownAction} from "redux";

import {Country} from "@pg-rp/common-types";

import {withRouteHandler} from "../../client/with_route_handler";
import {ISentLeadActions, setSentLeadValuesAction} from "../application/reducers/sent_lead_reducer";
import {getSentLeadFormValuesFromStorage} from "../application/utils/lead/sent_lead_form_values";
import {afterRedirectOauthLogin} from "../auth/actions/after_redirect_ouath_login";
import {AuthModal} from "../auth/components/AuthModal";
import {RegistrationFlow} from "../auth/components/RegistrationFlow";
import {useCloseRegistrationFlowOnRouteChange} from "../auth/hooks/use_close_registration_flow_on_route_change";
import {useGoogleOneTap} from "../auth/hooks/use_google_one_tap";
import {loadFavouritesToStore} from "../favorite/actions/load_favourites_to_store";
import {useSessionTracking} from "../hooks/use_session_tracking";
import {Footer} from "../navigation/components/Footer";
import {Navigation} from "../navigation/components/Navigation";
import {setNavigationState} from "../navigation/state/navigation_slice";
import {NewsletterSubscriptionModal} from "../newsletter/components/newsletter_subscription_modal/NewsletterSubscriptionModal";
import {Notifications} from "../notifications/components/Notifications";
import {useNotificationsRouteHandler} from "../notifications/utils/use_notifications_route_handler";
import {SemanticSearchModal} from "../semantic_search/components/SemanticSearchModal";
import {tdaDesktopHeight} from "../smart_ad_server/constants/tda_desktop_height";
import {tdaPortalId} from "../smart_ad_server/constants/tda_portal_id";
import {AmplitudeUserTracker} from "../tracking/amplitude/AmplitudeUserTracker";
import {useIsAuthenticated} from "../user/hooks/use_is_authenticated";
import {ViewType} from "../view_type/ViewType";
import {Awards} from "./components/Awards";
import {BrowserSupport} from "./components/BrowserSupport";
import {ScrollToTop} from "./components/ScrollToTop";
import {TdaServerSide} from "./components/TdaServerSide";
import {useLayoutOptions} from "./hooks/use_layout_options";
import {IVisualRoute} from "./types/IVisualRoute";
import {Routes} from "./Routes";
import {IRPStore} from "./rp_reducer";

export const Layout = withRouteHandler((props: {routes: IVisualRoute[]}) => {
    const dispatch: Dispatch<ISentLeadActions | UnknownAction> = useDispatch();

    const isAuthenticated = useIsAuthenticated();

    const isAuthenticatedRef = useRef(isAuthenticated);

    useSessionTracking();
    useNotificationsRouteHandler();

    useEffect(() => {
        if (isAuthenticatedRef.current !== isAuthenticated) {
            //  This is the place to add any global logic that requires to run on authentication status change

            dispatch(loadFavouritesToStore());
        }

        isAuthenticatedRef.current = isAuthenticated;
    }, [isAuthenticated]);

    useEffect(() => {
        dispatch(afterRedirectOauthLogin(isAuthenticated));

        /**
         * State of sentLeadFormValues is based on localStorage, so we need to add it after first mount in client.
         * It's because localStorage is not available on the server, so it will be undefined, and we need to avoid
         * ssr error (JSX mismatch).
         */

        const sentLeadValuesFromStorage = getSentLeadFormValuesFromStorage();

        if (sentLeadValuesFromStorage) {
            dispatch<ISentLeadActions>(setSentLeadValuesAction(sentLeadValuesFromStorage));
        }

        //some users have two cookies "algolytics_uuid", this affects the assignment of recommendations to users, recommendations are assigned to "algolytics_uuuid" if they are assigned to the wrong uuid they will not be visible in the konto-mieszkaniowe
        document.cookie = "algolytics_uuid=; Domain=.rynekpierwotny.pl; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/";
    }, []);

    const pinHandlers = useMemo(
        () => ({
            onPin: () => dispatch(setNavigationState("pinned")),
            onUnpin: () => dispatch(setNavigationState("unpinned")),
            onUnfix: () => dispatch(setNavigationState("unfixed"))
        }),
        [dispatch, setNavigationState]
    );

    const viewTypeAbroad = ViewType.OFFER_LIST_ABROAD;
    const viewTypeOfferList = ViewType.OFFER_LIST;

    const viewType = useSelector((state: IRPStore) => state.viewType.current);
    const blendTopOffset = viewType === viewTypeOfferList || viewType === viewTypeAbroad ? `${tdaDesktopHeight}px` : undefined;

    const country = useSelector((state: IRPStore) => state.investmentOffer.list.offers.latestQuery.country);
    const isSpanishView = viewType === ViewType.FOR_INVESTMENT_LIST && typeof country === "string" && parseInt(country) === Country.SPAIN;

    // Google One Tap / Google YOLO
    useGoogleOneTap();
    useCloseRegistrationFlowOnRouteChange();

    const layoutOptions = useLayoutOptions(props.routes);

    return (
        <>
            <AmplitudeUserTracker />
            <ScrollToTop />
            <BrowserSupport />

            <Notifications />

            {(viewType === ViewType.OFFER_LIST || viewType === ViewType.OFFER_LIST_ABROAD || isSpanishView) && (
                <div id={tdaPortalId} className={tdaStyle}>
                    <TdaServerSide />
                </div>
            )}
            {!layoutOptions.hideNavigation && (
                <Navigation onPin={pinHandlers.onPin} onUnpin={pinHandlers.onUnpin} onUnfix={pinHandlers.onUnfix} blendTopOffset={blendTopOffset} />
            )}
            <main>
                <Routes routes={props.routes} />
            </main>
            <AuthModal />

            <SemanticSearchModal />
            <RegistrationFlow />
            <NewsletterSubscriptionModal />

            {!layoutOptions.hideFooter && <Awards />}
            {!layoutOptions.hideFooter && <Footer />}
        </>
    );
});

const tdaStyle = css({
    display: "none",

    md: {
        display: "block",
        height: "22rem"
    }
});
import {useEffect, useMemo, useRef} from "react";
import {useDispatch, useSelector} from "react-redux";
import {css} from "@panda/css";
import type {Dispatch, UnknownAction} from "redux";

import {Country} from "@pg-rp/common-types";

import {withRouteHandler} from "../../client/with_route_handler";
import {ISentLeadActions, setSentLeadValuesAction} from "../application/reducers/sent_lead_reducer";
import {getSentLeadFormValuesFromStorage} from "../application/utils/lead/sent_lead_form_values";
import {afterRedirectOauthLogin} from "../auth/actions/after_redirect_ouath_login";
import {AuthModal} from "../auth/components/AuthModal";
import {RegistrationFlow} from "../auth/components/RegistrationFlow";
import {useCloseRegistrationFlowOnRouteChange} from "../auth/hooks/use_close_registration_flow_on_route_change";
import {useGoogleOneTap} from "../auth/hooks/use_google_one_tap";
import {loadFavouritesToStore} from "../favorite/actions/load_favourites_to_store";
import {useSessionTracking} from "../hooks/use_session_tracking";
import {Footer} from "../navigation/components/Footer";
import {Navigation} from "../navigation/components/Navigation";
import {setNavigationState} from "../navigation/state/navigation_slice";
import {NewsletterSubscriptionModal} from "../newsletter/components/newsletter_subscription_modal/NewsletterSubscriptionModal";
import {Notifications} from "../notifications/components/Notifications";
import {useNotificationsRouteHandler} from "../notifications/utils/use_notifications_route_handler";
import {SemanticSearchModal} from "../semantic_search/components/SemanticSearchModal";
import {tdaDesktopHeight} from "../smart_ad_server/constants/tda_desktop_height";
import {tdaPortalId} from "../smart_ad_server/constants/tda_portal_id";
import {AmplitudeUserTracker} from "../tracking/amplitude/AmplitudeUserTracker";
import {useIsAuthenticated} from "../user/hooks/use_is_authenticated";
import {ViewType} from "../view_type/ViewType";
import {Awards} from "./components/Awards";
import {BrowserSupport} from "./components/BrowserSupport";
import {ScrollToTop} from "./components/ScrollToTop";
import {TdaServerSide} from "./components/TdaServerSide";
import {useLayoutOptions} from "./hooks/use_layout_options";
import {IVisualRoute} from "./types/IVisualRoute";
import {Routes} from "./Routes";
import {IRPStore} from "./rp_reducer";

export const Layout = withRouteHandler((props: {routes: IVisualRoute[]}) => {
    const dispatch: Dispatch<ISentLeadActions | UnknownAction> = useDispatch();

    const isAuthenticated = useIsAuthenticated();

    const isAuthenticatedRef = useRef(isAuthenticated);

    useSessionTracking();
    useNotificationsRouteHandler();

    useEffect(() => {
        if (isAuthenticatedRef.current !== isAuthenticated) {
            //  This is the place to add any global logic that requires to run on authentication status change

            dispatch(loadFavouritesToStore());
        }

        isAuthenticatedRef.current = isAuthenticated;
    }, [isAuthenticated]);

    useEffect(() => {
        dispatch(afterRedirectOauthLogin(isAuthenticated));

        /**
         * State of sentLeadFormValues is based on localStorage, so we need to add it after first mount in client.
         * It's because localStorage is not available on the server, so it will be undefined, and we need to avoid
         * ssr error (JSX mismatch).
         */

        const sentLeadValuesFromStorage = getSentLeadFormValuesFromStorage();

        if (sentLeadValuesFromStorage) {
            dispatch<ISentLeadActions>(setSentLeadValuesAction(sentLeadValuesFromStorage));
        }

        //some users have two cookies "algolytics_uuid", this affects the assignment of recommendations to users, recommendations are assigned to "algolytics_uuuid" if they are assigned to the wrong uuid they will not be visible in the konto-mieszkaniowe
        document.cookie = "algolytics_uuid=; Domain=.rynekpierwotny.pl; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/";
    }, []);

    const pinHandlers = useMemo(
        () => ({
            onPin: () => dispatch(setNavigationState("pinned")),
            onUnpin: () => dispatch(setNavigationState("unpinned")),
            onUnfix: () => dispatch(setNavigationState("unfixed"))
        }),
        [dispatch, setNavigationState]
    );

    const viewTypeAbroad = ViewType.OFFER_LIST_ABROAD;
    const viewTypeOfferList = ViewType.OFFER_LIST;

    const viewType = useSelector((state: IRPStore) => state.viewType.current);
    const blendTopOffset = viewType === viewTypeOfferList || viewType === viewTypeAbroad ? `${tdaDesktopHeight}px` : undefined;

    const country = useSelector((state: IRPStore) => state.investmentOffer.list.offers.latestQuery.country);
    const isSpanishView = viewType === ViewType.FOR_INVESTMENT_LIST && typeof country === "string" && parseInt(country) === Country.SPAIN;

    // Google One Tap / Google YOLO
    useGoogleOneTap();
    useCloseRegistrationFlowOnRouteChange();

    const layoutOptions = useLayoutOptions(props.routes);

    return (
        <>
            <AmplitudeUserTracker />
            <ScrollToTop />
            <BrowserSupport />

            <Notifications />

            {(viewType === ViewType.OFFER_LIST || viewType === ViewType.OFFER_LIST_ABROAD || isSpanishView) && (
                <div id={tdaPortalId} className={tdaStyle}>
                    <TdaServerSide />
                </div>
            )}
            {!layoutOptions.hideNavigation && (
                <Navigation onPin={pinHandlers.onPin} onUnpin={pinHandlers.onUnpin} onUnfix={pinHandlers.onUnfix} blendTopOffset={blendTopOffset} />
            )}
            <main>
                <Routes routes={props.routes} />
            </main>
            <AuthModal />

            <SemanticSearchModal />
            <RegistrationFlow />
            <NewsletterSubscriptionModal />

            {!layoutOptions.hideFooter && <Awards />}
            {!layoutOptions.hideFooter && <Footer />}
        </>
    );
});

const tdaStyle = css({
    display: "none",

    md: {
        display: "block",
        height: "22rem"
    }
});
